import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { ConnectedRouter } from 'connected-react-router'
import { Redirect, Route, Switch } from 'react-router'
import CssBaseline from '@material-ui/core/CssBaseline'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AdminAuthGate from '../auth/components/AdminAuthGate'
import { SnackbarProvider } from 'notistack'
import { useTitle } from 'react-use'

import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'

import DrawerBar from '../components/Drawer'
import Notifications from '../notifications/Notifications'
import StandaloneKiosk from '../kiosk/StandaloneKiosk'
import ErrorBoundary from 'core/ErrorBoundary'
import ModalPromptProvider from 'civic-champs-shared/core/modal/ModalPromptProvider'
import FeaturesLoadedGate from 'core/feature/components/FeaturesLoadedGate'
import AuthInitializedGate from 'auth/components/AuthInitializedGate'
import { UISettingsProvider } from 'contexts/UISettingsProvider'
import useGoogleAnalytics from 'civic-champs-shared/utils/useGoogleAnalytics'
import useChatWidget from 'civic-champs-shared/utils/useChatWidget'

import useRoutes from './hooks/useRoutes'
import { OverlayContextProvider } from 'components/overlay/OverlayContext'
import PageHeader from 'App/components/PageHeader'
import InternationalizationBootstrap from 'i18n/components/InternationalizationBootstrap'
import { useAdminI18n } from 'i18n/feature'

const { REACT_APP_ENV } = process.env
const env = REACT_APP_ENV || 'development'
const documentTitle = env === 'production' ? `${document.title}` : `${env.toUpperCase()} - ${document.title}`

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: '100%',
  },
  content: {
    overflowX: 'auto',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: `24px 90px`,
    [theme.breakpoints.down('sm')]: {
      padding: '24px 90px 24px 30px',
    },
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    position: 'relative',
  },
}))

const ErrorFallback = ({ error }) => {
  const { t, enabled: i18nEnabled } = useAdminI18n('admin')

  return (
    <>
      <Typography variant="h4">
        {t('errors.boundaryTitle', { defaultValue: 'An Error Occurred. Please Refresh This Page.' })}
      </Typography>
      {i18nEnabled && (
        <Typography variant="body2">
          {t('errors.boundaryHint', { defaultValue: 'If the problem continues, contact support.' })}
        </Typography>
      )}
      {error && <Typography color="error">{error}</Typography>}
    </>
  )
}

const App = ({ isKioskMode, history }) => {
  const classes = useStyles()
  const { t } = useAdminI18n('admin')
  const { routes, defaultRoute } = useRoutes()
  useGoogleAnalytics()
  const { pingWidget } = useChatWidget()

  useTitle(documentTitle)

  useEffect(() => {
    pingWidget()

    return history.listen(() => {
      pingWidget()
    })
  }, [history, pingWidget])

  return (
    <ConnectedRouter history={history}>
      <AdminAuthGate>
        <UISettingsProvider>
          <SnackbarProvider>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <Notifications />
              <ModalPromptProvider>
                <OverlayContextProvider>
                  {isKioskMode ? (
                    <StandaloneKiosk />
                  ) : (
                    <>
                      <div className={classes.root}>
                        <CssBaseline />
                        <DrawerBar history={history} />
                        <main className={classes.content}>
                          <ErrorBoundary fallback={ErrorFallback}>
                            <AuthInitializedGate>
                              <FeaturesLoadedGate
                                fallback={() => (
                                  <PageHeader title={t('loading.title', { defaultValue: 'Civic Champs' })}>
                                    <div />
                                  </PageHeader>
                                )}
                              >
                                <InternationalizationBootstrap />
                                <Switch>
                                  {routes.map((props, index) => (
                                    <Route {...props} key={index} />
                                  ))}
                                  <Redirect to={defaultRoute} />
                                </Switch>
                              </FeaturesLoadedGate>
                            </AuthInitializedGate>
                          </ErrorBoundary>
                        </main>
                      </div>
                    </>
                  )}
                </OverlayContextProvider>
              </ModalPromptProvider>
            </MuiPickersUtilsProvider>
          </SnackbarProvider>
        </UISettingsProvider>
      </AdminAuthGate>
    </ConnectedRouter>
  )
}

App.propTypes = {
  history: PropTypes.object,
}

export default App
